import { useQuery } from '@tanstack/react-query';
import { getDefaultDashboardListRow } from 'components/foundation/dashboard/dashboard-list/DashboardList/DashboardList';
import { collapseTimeLogFeedLogs } from 'containers/time-log-feed/utils/utils';
import { gql } from 'graphql-request';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { DateTime } from 'luxon';
import { useCallback } from 'react';
import { takeWithPopulate } from 'utils/collectionUtils';
import { useTimeLogFeedDashboardDataMapper } from '../useTimeLogFeedDashboardDataMapper/useTimeLogFeedDashboardDataMapper';

const FEED_LOOKBACK_DAYS = 60;
// Collapsing groups raw logs by time entry, so fetch more than the 4 rows shown to survive collapse.
const FEED_FETCH_COUNT = 8;

const TIME_LOG_FEED_DASHBOARD_QUERY = gql`
  query TimeLogFeedDashboard($startTime: Timestamp!, $endTime: Timestamp!, $first: Int!) {
    dashboard {
      timeLogFeed(startTime: $startTime, endTime: $endTime, first: $first) {
        id
        createdOn
        actionType
        deviceType
        startLocationId
        endLocationId
        timeEntryId
        memberId
        updaterMemberId
        member {
          id
          firstName
          lastName
        }
        updaterMember {
          id
          firstName
          lastName
        }
        timeEntry {
          breaks {
            id
            clientLogs {
              id
              clientTimeEntryLogId
              actionType
              startLocationId
              endLocationId
            }
          }
        }
      }
    }
  }
`;

export default function useTimeLogFeedDashboardData(enabled: boolean = true) {
  const dataMapper = useTimeLogFeedDashboardDataMapper();
  const graphQLClient = useGraphQLClient();
  const baseKey = useReactQueryBaseKey();

  const { data, isLoading, refetch } = useQuery({
    queryKey: [...baseKey, 'time-log-feed-dashboard'],
    queryFn: async () => {
      const startTime = DateTime.local().minus({ days: FEED_LOOKBACK_DAYS }).startOf('day').toUTC().toISO();
      const endTime = DateTime.local().toUTC().toISO();

      const result = await graphQLClient.request<{ dashboard: { timeLogFeed: unknown[] } }>({
        document: TIME_LOG_FEED_DASHBOARD_QUERY,
        variables: { startTime, endTime, first: FEED_FETCH_COUNT },
      });

      const logs = result.dashboard.timeLogFeed ?? [];

      const collapsed = collapseTimeLogFeedLogs(logs as any);

      const results = collapsed.map(dataMapper);
      return takeWithPopulate(results, 4, () => ({
        ...getDefaultDashboardListRow(),
        sublabel: null,
        grouping: null,
      }));
    },
    enabled,
  });

  const execute = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return { data: data ?? [], loading: isLoading, execute };
}
